import { Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Main from "../Main";
import AppBreadcrumbs from "../../components/ui/AppBreadcrumbs";
import { useWallet } from "../../context/walletContext";
import { formatAmount } from "../../utils/formatAmount";
import {
  fetchSingleBeneficiaryAccount,
  withdrawDMTBalanceByBeneficiary,
  withdrawDMTBalanceByBeneficiaryOtp
} from "../../api/remitterApi";
import { getLocation } from "../../components/Navigator/Navigator";
import { useNotify } from "../../context/NotifyContext";
import { setLocation } from "../../features/authSlice";
import TransactionReceiptModal from "../../components/TransactionReceiptModal";
import useBankVerification from "../../hooks/useBankVerification";

const OTP_STORAGE_KEY = process.env.OTP_STORAGE_KEY || import.meta?.env?.OTP_STORAGE_KEY || "dmtOtpSession";

const MoneyTransfertoBank = () => {

    const { location: savedLocation } = useSelector((state) => state.auth); // get location from Redux
const dispatch = useDispatch();
const [fetchingLocation, setFetchingLocation] = useState(false);

  const navigate = useNavigate();
  const { userInfo } = useSelector(state => state.auth);
  const { id } = useParams();
  const { notify } = useNotify();
  const [search] = useSearchParams();
  const phone = search.get("phone");

  const { walletAmt, refreshWalletCart } = useWallet();

  const [settalmentSingleAcc, setSingleSettalmentAcc] = useState({});
  const [amount, setTransactionAmount] = useState("");
  const [mode, setMode] = useState("IMPS");
  const [loading, setLoading] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
const [txnData, setTxnData] = useState(null);
const bankVerification = useBankVerification({
  sourceType: "BENEFICIARY",
  sourceId: settalmentSingleAcc?._verificationSourceId,
  bankId: settalmentSingleAcc?._verificationBankId,
});
const verificationStatus = bankVerification.result?.verificationStatus || "UNVERIFIED";
const bankVerified = verificationStatus === "VERIFIED";
  // OTP State
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(90);
  const [txnSession, setTxnSession] = useState({ payload: null, txnRef: null, used: false });

  // ✅ Load OTP session from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(OTP_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!parsed.used && parsed.expiresAt > Date.now()) {
        setTxnSession(parsed);
        setShowOtp(true);
        setTimer(Math.floor((parsed.expiresAt - Date.now()) / 1000));
      } else {
        localStorage.removeItem(OTP_STORAGE_KEY);
      }
    }
  }, []);

  // OTP Timer
  useEffect(() => {
    if (!showOtp) return;
    if (timer <= 0) {
      notify("OTP expired", "error");
      setShowOtp(false);
      localStorage.removeItem(OTP_STORAGE_KEY);
      setTimer(90);
      setTxnSession({ payload: null, txnRef: null, used: false });
      return;
    }
    const interval = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [showOtp, timer, notify]);

  // Fetch Beneficiary
  const fetch_settalmentAcc = async (id) => {
    try {
      if (!id) return notify("Invalid Bank ID", "error");
      if (!phone || phone.length !== 10) return notify("Invalid Remitter Phone", "error");

      const result = await fetchSingleBeneficiaryAccount(id, { phone });
      if (result?.success) {
        setSingleSettalmentAcc({
          ...(result?.data?.benBank || {}),
          _verificationSourceId: result?.data?.sourceId,
          _verificationBankId: result?.data?.bankId,
        });
      }
      else notify(result?.message || "Failed to fetch bank details", "error");
    } catch {
      notify("Something went wrong!", "error");
    }
  };

  useEffect(() => { fetch_settalmentAcc(id); }, [id]);

  // Amount input
  const handleChange = (e) => {
    let value = e.target.value;
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    value = Number(value);
    if (value <= 0) return;
    if (walletAmt?.main?.balance && value > walletAmt.main.balance) {
      notify("Amount exceeds available balance", "error");
      value = walletAmt.main.balance;
    }
    setTransactionAmount(value);
  };



const handleSubmit = async (e) => {
  e.preventDefault();

  if (!settalmentSingleAcc?.accno) return notify("Beneficiary account not found", "error");
  if (!id) return notify("Invalid Bank", "error");
  if (!amount || amount <= 0) return notify("Enter valid transaction amount", "error");
  if (walletAmt?.main?.balance < amount) return notify("Insufficient wallet balance", "error");
  if (txnSession.used) return notify("Transaction already processed", "warning");

  setFetchingLocation(true);
  let location;

  try {
    // ✅ Check Redux first
    if (savedLocation) {
      location = savedLocation;
      console.log("Using saved location from Redux:", location);
    } else {
      // ✅ If not in Redux, fetch geolocation
      location = await getLocation();
      dispatch(setLocation(location)); // store in Redux for future use
      console.log("Fetched location and stored in Redux:", location);
    }
  } catch {
    setFetchingLocation(false);
    return notify("Location permission required", "error");
  }

  setFetchingLocation(false);

  const payload = {
    bankId: id,
    acco: settalmentSingleAcc?.accno,
    amount,
    serviceType: "dmt",
    remitterPhone: settalmentSingleAcc?.phone,
    mode: mode || "IMPS",
    latitude: location?.latitude,
    longitude: location?.longitude,
    state: location?.state || "",
  };

  try {
    setLoading(true);
    const res = await withdrawDMTBalanceByBeneficiaryOtp(payload);

    if (res?.success) {
      notify(res?.message || "OTP sent successfully", "success");

      const sessionData = {
        payload,
        txnRef: res?.data?.referenceid,
        expiresAt: Date.now() + 90 * 1000,
        used: false
      };
      setTxnSession(sessionData);
      localStorage.setItem(OTP_STORAGE_KEY, JSON.stringify(sessionData));
      setShowOtp(true);
      setTimer(90);
    } else {
      notify(res?.message || "Transaction failed", "error");
    }
  } catch {
    notify("Server error during transaction", "error");
  } finally {
    setLoading(false);
  }
};

const handlePennyDropVerify = async () => {
  try {
    await bankVerification.verify();
    refreshWalletCart();
  } catch (error) {
    notify(error?.message || "Verification failed", "error");
  }
};





const submitOtp = async () => {
  if (!otp) return notify("Enter valid OTP", "error");
  if (!txnSession.txnRef) return notify("Invalid session, retry transaction", "error");

  try {
    setLoading(true);

    const res = await withdrawDMTBalanceByBeneficiary({
      referenceid: txnSession.txnRef,
      otp,
    });

    if (res?.data?.success) {
      notify(res?.message || "Transaction successful", "success");


      setTxnData(res?.data?.referenceid);

      // ✅ Open receipt popup
      setOpenReceipt(true);

      refreshWalletCart();
     

      // ✅ Clear OTP session only on success
      localStorage.removeItem(OTP_STORAGE_KEY);
      setTxnSession({ payload: null, txnRef: null, used: true });
      setShowOtp(false); // Close modal only after success
      setTimer(90);
    } else {
      // ❌ Show backend error but keep modal open
      notify(res?.message || "Transaction failed", "error");
      // Keep OTP input intact, allow retry
    }
  } catch (err) {
    notify(err?.message || "Server error", "error");
    // Keep modal open for retry
  } finally {
    setLoading(false);
  }
};


  const cancelOtp = () => {
    setShowOtp(false);
    localStorage.removeItem(OTP_STORAGE_KEY);
    setTxnSession({ payload: null, txnRef: null, used: false });
    setTimer(90);
  };

  return (
    <Main showRightPanel={false}>
      <div className="mt-4 font-[Poppins] ">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "MATM Module" },
            { label: "Settlements" },
          ]}
        />

        {/* Wallet info */}
        <div className=" shadow-sx border border-gray-200 rounded-lg p-2 mb-4 mt-4 flex flex-wrap md:flex-nowrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={userInfo?.logo} alt="profile" className="h-10 w-10 rounded-md object-cover" />
            <div className="flex flex-col md:flex-row md:items-center md:gap-2 whitespace-nowrap">
              <p className="text-[15px] font-semibold">{userInfo?.name || "User"}</p>
              <p className="hidden md:block text-[18px] font-semibold">|</p>
              <p className="text-[12px] md:text-[15px] font-semibold">ID: {userInfo?.userName || "123456"}</p>
            </div>
          </div>
          <div className="text-right font-semibold  text-[10px] md:text-[15px] leading-tight w-full md:w-auto">
            MONEY WALLET BALANCE :
            <span className=" font-bold ml-1 text-[14px] md:text-[16px]">
              ₹ {formatAmount(walletAmt?.main?.balance)}
            </span>
          </div>
        </div>

        {/* Transfer Form */}
        <div className=" shadow-sm rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-5 border-b pb-2">Money Transfer Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Account Holder */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">ACCOUNT HOLDER NAME <span className="text-red-500">*</span></label>
              <input type="text" value={settalmentSingleAcc?.name} disabled className="border px-3 py-2 rounded-md bg-gray-100"/>
            </div>

<div className="flex flex-col">
  <label className="text-sm font-medium mb-1">
    ACCOUNT VERIFICATION
  </label>

  <div className="flex gap-2 items-center">
    <Button
      onClick={handlePennyDropVerify}
      disabled={
        bankVerification.loading ||
        bankVerification.statusLoading ||
        bankVerified ||
        bankVerification.pending ||
        !bankVerification.validBankDetails
      }
      variant="contained"
      color={bankVerified ? "success" : "primary"}
      size="small"
    >
      {bankVerification.loading ? (
        <CircularProgress size={18} sx={{ color: "#fff" }} />
      ) : bankVerification.bypassed ? (
        "Verification Bypassed"
      ) : bankVerified ? (
        "Bank Verified"
      ) : bankVerification.pending ? (
        "Verification Pending"
      ) : verificationStatus === "FAILED" ? (
        "Verification Failed"
      ) : (
        "Verify Bank"
      )}
    </Button>

    {bankVerified && (
      <span className="text-green-600 text-sm font-semibold">
        ✔ {bankVerification.result?.beneficiaryName || settalmentSingleAcc?.name}
      </span>
    )}
  </div>
  {verificationStatus === "FAILED" && bankVerification.result?.failureReason && (
    <p className="mt-1 text-sm text-red-600">{bankVerification.result.failureReason}</p>
  )}
</div>


            {/* Bank Name */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">BANK NAME <span className="text-red-500">*</span></label>
              <input type="text" value={settalmentSingleAcc.bankname} disabled className="border px-3 py-2 rounded-md bg-gray-100"/>
            </div>
            {/* Account Number */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">ACCOUNT NUMBER <span className="text-red-500">*</span></label>
              <input type="text" value={settalmentSingleAcc?.accountNumber || settalmentSingleAcc?.accno} disabled className="border px-3 py-2 rounded-md bg-gray-100"/>
            </div>
            {/* IFSC */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">IFSC CODE <span className="text-red-500">*</span></label>
              <input type="text" value={settalmentSingleAcc.ifsc} disabled className="border px-3 py-2 rounded-md bg-gray-100"/>
            </div>
            {/* Transaction Amount */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">TRANSACTION AMOUNT <span className="text-red-500">*</span></label>
              <input type="number" min="0" max={walletAmt?.main?.balance} value={amount} onChange={handleChange} placeholder="Enter Amount to Transfer" className={`${!amount ? "border border-red-400" : "border"} border px-3 py-2 rounded-md`}/>
            </div>
            {/* Mode */}
            <div className="flex flex-col">
              <label className="text-sm font-medium mb-1">TRANSFER MODE <span className="text-red-500">*</span></label>
              <select className="border px-3 py-2 rounded-md overflow-hidden" value={mode} onChange={e => setMode(e.target.value)}>
                <option value="IMPS">IMPS</option>
                <option value="NEFT">NEFT</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-4 sm:gap-6 mt-4 w-full">
            <button onClick={() => navigate("/money")} className="w-full sm:w-[180px] h-10 bg-[#FF000026] text-[#FF0000] rounded-md font-semibold text-[13px] flex items-center justify-center transition-all hover:bg-[#FF000040]">CANCEL</button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || fetchingLocation} 
              sx={{
                width: { xs: "100%", sm: "180px" }, height: "40px",
                backgroundColor: "#1E9C3226", color: "#1E9C32",
                fontWeight: 600, fontSize: "13px", borderRadius: "6px",
                display: "flex", alignItems: "center", justifyContent: "center",
                "&:hover": { backgroundColor: "#1E9C3250" },
              }}
            >
              {fetchingLocation ? <CircularProgress size={20} sx={{ color: "#1E9C32" }} /> : "TRANSFER"}
            </Button>
          </div>
        </div>

        <TransactionReceiptModal
  open={openReceipt}
  onClose={() => {
    setOpenReceipt(false);
    navigate("/money"); // optional redirect after close
  }}
  txnId={txnData}
/>


        {/* OTP Modal */}
        {showOtp && (
          <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
            <div className="bg-white text-black p-6 rounded-lg w-[320px]">
              <h3 className="font-semibold mb-2">Enter OTP</h3>
              <input className="border w-full p-2 mb-2" value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter Your Otp"/>
              <p className="text-sm mb-3">Expires in {timer}s</p>
              <div className="flex gap-2">
                <Button variant="outlined" onClick={cancelOtp} fullWidth>Cancel</Button>
                <Button variant="contained" color="success" onClick={submitOtp} fullWidth disabled={loading}>Verify</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Main>
  );
};

export default MoneyTransfertoBank;
